import { FC, useMemo } from 'react';
import { useAtom, useAtomValue } from 'jotai';
import { createColumnHelper, ColumnDef } from '@tanstack/react-table';
import {
  Pagination,
  DataTableSkeleton,
  InlineNotification,
} from '@carbon/react';
import { PageHeader } from 'components/page-header';
import { formatNumber } from 'utils/number';
import { StatsBar } from 'components/stats-bar';
import { DataTable } from 'components/data-table';
import {
  subtractsMissingTirsLoadableAtom,
  pageAtom,
  pageSizeAtom,
} from './atoms';
import type { SubtractMissingTir } from './types';
import styles from './page.module.scss';

const TABLE_CONFIG = {
  sorting: true,
  globalSearch: true,
  columnFilters: false,
  columnResizing: true,
  columnReordering: true,
  columnPinning: true,
  columnVisibility: true,
} as const;

const columnHelper = createColumnHelper<SubtractMissingTir>();

const columns = [
  columnHelper.accessor('subtractId', {
    id: 'subtractId',
    header: 'Subtract ID',
    size: 180,
    cell: (info) => {
      const href = info.row.original.salesforceHref;
      const value = info.getValue();
      if (!href) return value ?? '--';
      return (
        <a href={href} target="_blank" rel="noreferrer">
          {value}
        </a>
      );
    },
  }),
  columnHelper.accessor('subtractName', {
    id: 'subtractName',
    header: 'Subtract Name',
    size: 180,
  }),
  columnHelper.accessor('tractName', {
    id: 'tractName',
    header: 'Tract Name',
    size: 180,
    cell: (info) => {
      const href = info.row.original.tractSalesforceHref;
      const name = info.getValue();
      if (!href) return name ?? '--';
      return (
        <a href={href} target="_blank" rel="noreferrer">
          {name}
        </a>
      );
    },
  }),
  columnHelper.accessor('instrumentType', {
    id: 'instrumentType',
    header: 'Instrument Type',
    size: 140,
    cell: (info) => info.getValue() ?? '--',
  }),
  columnHelper.accessor('subtractNetAcres', {
    id: 'subtractNetAcres',
    header: 'Net Acres',
    size: 110,
    cell: (info) => {
      const v = info.getValue();
      return v !== null && v !== undefined ? formatNumber(v, 0, 2) : '--';
    },
  }),
  columnHelper.accessor('subtractGrossAcres', {
    id: 'subtractGrossAcres',
    header: 'Gross Acres',
    size: 110,
    cell: (info) => {
      const v = info.getValue();
      return v !== null && v !== undefined ? formatNumber(v, 0, 2) : '--';
    },
  }),
  columnHelper.accessor('hasBoso', {
    id: 'hasBoso',
    header: 'Has BOSO',
    size: 100,
    cell: (info) => (info.getValue() ? 'Yes' : 'No'),
  }),
  columnHelper.accessor('subtractCreatedAt', {
    id: 'subtractCreatedAt',
    header: 'Subtract Created',
    size: 200,
    cell: (info) => {
      const value = info.getValue();
      return value ? new Date(value).toLocaleString() : '--';
    },
  }),
  columnHelper.accessor('subtractUpdatedAt', {
    id: 'subtractUpdatedAt',
    header: 'Subtract Updated',
    size: 200,
    cell: (info) => {
      const value = info.getValue();
      return value ? new Date(value).toLocaleString() : '--';
    },
  }),
  columnHelper.accessor('geometryCreatedAt', {
    id: 'geometryCreatedAt',
    header: 'Geometry Created',
    size: 200,
    cell: (info) => {
      const value = info.getValue();
      return value ? new Date(value).toLocaleString() : '--';
    },
  }),
  columnHelper.accessor('geometryUpdatedAt', {
    id: 'geometryUpdatedAt',
    header: 'Geometry Updated',
    size: 200,
    cell: (info) => {
      const value = info.getValue();
      return value ? new Date(value).toLocaleString() : '--';
    },
  }),
];

export const SubtractsMissingTirsPage: FC = () => {
  const loadable = useAtomValue(subtractsMissingTirsLoadableAtom);
  const [page, setPage] = useAtom(pageAtom);
  const [, setPageSize] = useAtom(pageSizeAtom);

  const data = useMemo(() => {
    return loadable.state === 'hasData' ? (loadable.data?.data ?? []) : [];
  }, [loadable]);

  const pagination =
    loadable.state === 'hasData' ? loadable.data?.pagination : null;
  const stats = loadable.state === 'hasData' ? loadable.data?.stats : null;
  const isLoading = loadable.state === 'loading';
  const error =
    loadable.state === 'hasError'
      ? loadable.error instanceof Error
        ? loadable.error.message
        : 'Failed to fetch data'
      : null;

  return (
    <>
      <PageHeader
        breadcrumbs={[['/admin/dashboard', 'Admin']]}
        title="Subtracts Missing TIRs"
        actions={null}
      />
      <div className={styles.sectionHeader}>
        <div className={styles.sectionDescription}>
          Subtracts that spatially intersect at least one unit allocation tract
          that has allocation manager line items, but have no
          TractIntersectionResults. These are actionable — TIA should have
          produced results for them. Subtracts with no UAT overlap or whose
          overlapping UATs lack AMLIs are excluded.
        </div>
      </div>

      {isLoading && <DataTableSkeleton columnCount={5} rowCount={10} />}
      {error && (
        <InlineNotification
          kind="error"
          title="Error"
          subtitle={error}
          hideCloseButton
        />
      )}

      {!isLoading && !error && (
        <>
          {stats && (
            <StatsBar
              stats={[
                {
                  label: 'Missing TIRs',
                  value: stats.total,
                  tagType: stats.total > 0 ? 'red' : 'green',
                },
              ]}
            />
          )}

          <DataTable
            data={data}
            columns={columns as ColumnDef<SubtractMissingTir>[]}
            getRowId={(row) => row.subtractId}
            isLoading={isLoading}
            persistenceKey="admin-subtracts-missing-tirs"
            {...TABLE_CONFIG}
          />

          {pagination && pagination.totalPages > 1 && (
            <Pagination
              page={page}
              pageSize={pagination.pageSize}
              pageSizes={[100, 500, 1000]}
              totalItems={pagination.totalRecords}
              onChange={({
                page: newPage,
                pageSize: newPageSize,
              }: {
                page: number;
                pageSize: number;
              }) => {
                setPage(newPage);
                setPageSize(newPageSize);
              }}
            />
          )}
        </>
      )}
    </>
  );
};
